

import { BorderBox } from '@/components/atoms/BorderBox';
import { XlsxFileInput } from '@/components/atoms/XLSXInput';
import Combobox from '@/components/custom/Combobox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from "@/components/ui/button";
import {
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DialogRendererProps } from '@/lib/dynamic-dialog/types';
import { Tables } from '@/lib/supabase/supabase_types_override';
import { scrapUndefinedValuesFromObject } from '@/lib/utils/object';
import { zodResolver } from '@hookform/resolvers/zod';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ValidatedCrmProject } from '../../../hooks/lib/useValidateCrmProjects';
import { useImportConfigsDialogContext } from '../../../context/importConfigsContext/import-configs-dialog-context';
import { ImportConfigsDialogProvider } from '../../../context/importConfigsContext/import-configs-dialog-provider';
import { editConfigDialogTabSchema, importConfigsDialogConfigurationParametersSchema } from './ImportConfigsDialog.schema';
import { EditConfigDialogTabSchemaType, ImportConfigsDialogConfigurationParametersSchemaType } from './types';
import { useMassImportCrmProjectsWithConfigs } from '@/features/crm/hooks/useQuery/useMassImportCrmProjectsWithConfigs';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';

export interface ImportConfigsDialogProps {
    crm: Tables<'crm'>;
}


export type TabsValue =
    | 'import'
    | 'validate'
    | 'details'
    | 'edit';

export function ImportConfigsDialog({ crm, ...props }: Readonly<ImportConfigsDialogProps & DialogRendererProps>) {
    return (
        <DialogContent className='max-w-screen-xl'>
            <ImportConfigsDialogProvider
                crm={crm}
                dialogProps={props}
            >
                <ImportConfigsDialogTabs />
            </ImportConfigsDialogProvider>
        </DialogContent>
    )
}

function ImportConfigsDialogTabs() {
    const { currentTab } = useImportConfigsDialogContext();
    return (
        <div
        // className={cn({
        //     'max-w-[600px]': editedCrmConfig || editedCrmProjectConfig,
        //     'max-w-screen-xl': (currentTab === 'import' || currentTab === 'validate') && !editedCrmConfig && !editedCrmProjectConfig,
        // })}
        >
            <Tabs defaultValue='import' value={currentTab}>
                <TabsList className='grid grid-cols-2'>
                    <TabsTrigger value="import" disabled>Import file</TabsTrigger>
                    <TabsTrigger value="validate" disabled>Validate file</TabsTrigger>
                </TabsList>
                <ImportTab />
                <ValidateTab />
                <EditTab />
            </Tabs>
        </div>
    )
}

function ImportTab() {
    const {
        rawFileData,
        setRawFileData,
        dialogProps,
        crm,
        setCurrentTab,
        setFileObjectKeyToPropertyMap,
        fileObjectExampleValues,
        fileObjectKeys
    } = useImportConfigsDialogContext();
    const form = useForm<ImportConfigsDialogConfigurationParametersSchemaType>({
        resolver: zodResolver(importConfigsDialogConfigurationParametersSchema),
        mode: 'onChange',
    })
    const handleSubmit = (data: ImportConfigsDialogConfigurationParametersSchemaType) => {
        const scrappedObject = {
            ...data,
            crmProjectFilePropertyToParameterMap: data.crmProjectFilePropertyToParameterMap ? scrapUndefinedValuesFromObject(data.crmProjectFilePropertyToParameterMap) : undefined,
            crmConfigFilePropertyToParameterMap: data.crmConfigFilePropertyToParameterMap ? scrapUndefinedValuesFromObject(data.crmConfigFilePropertyToParameterMap) : undefined,
        } as ImportConfigsDialogConfigurationParametersSchemaType;
        setFileObjectKeyToPropertyMap?.(scrappedObject);
        setCurrentTab('validate');
    }
    return (
        <TabsContent value="import" className=''>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-2'>
                    <DialogHeader>
                        <DialogTitle className=''>
                            Importing configuration for CRM {crm?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <Separator />
                    <ScrollArea className='h-96'>
                        <div className="flex flex-col gap-2">
                            <hr />
                            <p>
                                Drop your file here or click to upload
                            </p>
                            <XlsxFileInput onFileLoad={(data) => setRawFileData(data as Record<string, string>[])} />
                            {rawFileData && (
                                <div className="flex flex-col gap-2">
                                    <BorderBox variant={'info'}>
                                        <p>Select which property from your file corresponds to which property in the CRM configuration</p>
                                    </BorderBox>
                                    <div className="flex flex-col gap-2">
                                        <p className='font-medium'>CRM Project Properties:</p>
                                        <BorderBox className="grid items-start grid-cols-3 gap-2">
                                            <FormField
                                                control={form.control}
                                                name="crmProjectName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className='text-xs'>CRM Project name *</FormLabel>
                                                        <FormControl>
                                                            <Select value={field.value} onValueChange={field.onChange}>
                                                                <SelectTrigger>
                                                                    {field.value ?? "Select property from your file"}
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {fileObjectKeys.map((key, index) => (
                                                                        <SelectItem
                                                                            key={key}
                                                                            value={key.trim()}
                                                                        >
                                                                            {key} <span className='text-xs italic text-muted-foreground'>(e.g. {String(fileObjectExampleValues[index])})</span>
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />

                                                    </FormItem>
                                                )}
                                            />
                                            {crm?.configuration?.parameter?.project?.map((param) => (
                                                <FormField
                                                    key={param.id}
                                                    control={form.control}
                                                    name={`crmProjectFilePropertyToParameterMap.${param.id}`}
                                                    render={({ field }) => (
                                                        <FormItem className=''>
                                                            <FormLabel className='text-xs'>{param.name}</FormLabel>
                                                            <FormControl>
                                                                <Select onValueChange={field.onChange} value={field.value}>
                                                                    <SelectTrigger>
                                                                        {field.value ?? "Select property from your file"}
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {fileObjectKeys.map((key, index) => (
                                                                            <SelectItem
                                                                                key={key}
                                                                                value={key.trim()}
                                                                            >
                                                                                {key} <span className='text-xs italic text-muted-foreground'>(e.g. {String(fileObjectExampleValues[index])})</span>
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </FormControl>
                                                            <FormMessage />

                                                        </FormItem>
                                                    )}
                                                />
                                            ))}
                                        </BorderBox>
                                        <p className='font-medium'>CRM Config Properties:</p>
                                        <BorderBox className="grid items-start grid-cols-3 gap-2">
                                            <FormField
                                                control={form.control}
                                                name="crmConfigName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel className='text-xs'>CRM Config name *</FormLabel>
                                                        <FormControl>
                                                            <Select value={field.value} onValueChange={field.onChange}>
                                                                <SelectTrigger>
                                                                    {field.value ?? "Select property from your file"}
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {fileObjectKeys.map((key, index) => (
                                                                        <SelectItem key={key} value={key}>
                                                                            {key} <span className='text-xs italic text-muted-foreground'>(e.g. {String(fileObjectExampleValues[index])})</span>
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            {crm?.configuration?.parameter?.survey?.map((param) => (
                                                <FormField
                                                    key={param.id}
                                                    control={form.control}
                                                    name={`crmConfigFilePropertyToParameterMap.${param.id}`}
                                                    render={({ field }) => (
                                                        <FormItem className=''>
                                                            <FormLabel className='text-xs'>{param.name}</FormLabel>
                                                            <FormControl>
                                                                <Select onValueChange={field.onChange} value={field.value}>
                                                                    <SelectTrigger>
                                                                        {field.value ?? "Select property from your file"}
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {fileObjectKeys.map((key, index) => (
                                                                            <SelectItem
                                                                                key={key}
                                                                                value={key.trim()}
                                                                            >
                                                                                <span>{key} </span><span className='text-xs italic text-muted-foreground'>(e.g. {String(fileObjectExampleValues[index])})</span>
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </FormControl>
                                                            <FormMessage />

                                                        </FormItem>
                                                    )}
                                                />
                                            ))}
                                        </BorderBox>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                    <DialogFooter className='mt-2'>
                        <Button
                            variant='secondary'
                            size='sm'
                            type='button'
                            onClick={() => { dialogProps.cancel() }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size='sm'
                            type='submit'
                            disabled={!rawFileData}
                        >
                            Next
                        </Button>
                    </DialogFooter>
                </form>
            </Form>
        </TabsContent>
    )
}

function ValidateTab() {
    const {
        dialogProps,
        isLoadingValidateProjects,
        setCurrentTab,
        validateProjects,
        validatedProjects,
        currentTab,
        crm
    } = useImportConfigsDialogContext();
    useEffect(() => {
        if (currentTab !== 'validate') return;
        validateProjects();
    }, [validateProjects, currentTab]);
    const parentRef = useRef(null)

    const virtualizer = useVirtualizer({
        count: validatedProjects?.length ?? 0,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 50,
        overscan: 5,
        gap: 8
    })
    const [openedAccordionItemId, setOpenedAccordionItemId] = useState<string | undefined>(undefined);
    const virtualizerItems = virtualizer.getVirtualItems();
    const { isPending: isPendingImport, mutateAsync: massImportCrmProjectsWithConfigs } = useMassImportCrmProjectsWithConfigs()
    return (
        <TabsContent value="validate" className='space-y-2'>
            <DialogHeader>
                <DialogTitle className=''>
                    Validate data
                </DialogTitle>
            </DialogHeader>
            <Separator />
            <div className="flex flex-col gap-2 pr-2 overflow-y-auto wissp-scroll-box h-96 [contain:strict]" ref={parentRef}>
                {isLoadingValidateProjects && (
                    <div className="grid w-full h-full place-items-center">
                        <Loader className='animate-spin infinite' />
                    </div>
                )}
                {validatedProjects && (
                    <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
                        <ul className="absolute top-0 left-0 w-full" style={{ transform: `translateY(${virtualizerItems[0]?.start ?? 0}px)` }}>
                            <Accordion
                                type='single'
                                onValueChange={(id) => {
                                    setOpenedAccordionItemId(id);
                                    setTimeout(() => virtualizer.measure(), 0);
                                }}
                                value={openedAccordionItemId}
                            >
                                {virtualizerItems.map((virtualizeItem) => (
                                    <li
                                        key={virtualizeItem.key}
                                        data-index={virtualizeItem.index}
                                        ref={virtualizer.measureElement}
                                        className='mb-2'
                                    >
                                        <ValidateTabProjectRow
                                            crmProject={validatedProjects[virtualizeItem.index]}
                                        />
                                    </li>
                                ))}
                            </Accordion>
                        </ul>
                    </div>
                )}
            </div>
            <DialogFooter className='mt-2'>
                <Button
                    variant='secondary'
                    size='sm'
                    type='button'
                    onClick={() => { dialogProps.cancel() }}
                >
                    Cancel
                </Button>
                <Button
                    variant='secondary'
                    size='sm'
                    type='button'
                    onClick={() => { setCurrentTab?.('import') }}
                >
                    Back
                </Button>
                <Button
                    size='sm'
                    type='submit'
                    disabled={isPendingImport || !validatedProjects || validatedProjects.length === 0 || !crm}
                    onClick={async () => {
                        if (!validatedProjects || !crm) return;
                        massImportCrmProjectsWithConfigs({
                            importedCrmProjects: validatedProjects,
                            crmId: crm.id
                        })
                            .then(() => {
                                SuccessPopup('CRM Projects and Configurations imported successfully.')
                                dialogProps.cancel()
                            })
                            .catch((err) => {
                                console.error(err);
                                ErrorPopup('An error occurred while importing CRM Projects and Configurations.')
                            })
                    }}
                >
                    {isPendingImport ? 'Importing...' : 'Finish'}
                </Button>
            </DialogFooter>
        </TabsContent>
    )
}

interface ValidateTabProjectRow {
    crmProject: ValidatedCrmProject
}

function ValidateTabProjectRow({ crmProject }: Readonly<ValidateTabProjectRow>) {
    const { setCurrentTab, setEditedConfigId, crm } = useImportConfigsDialogContext();
    const editHandler = (id: string) => () => {
        setEditedConfigId(id)
        setCurrentTab('edit');
    }
    return (
        <BorderBox size='sm' className='block'>
            <AccordionItem value={String(crmProject.id)} >
                <AccordionTrigger className='p-0 m-0'>
                    <div className="flex justify-between w-full pb-2 pr-2">
                        <p>{crmProject.name}</p>
                        <div className="ml-auto">
                            {(crmProject?.closeMatches?.length ?? 0) > 0 && (
                                <Badge className='mr-2 bg-yellow-500'>{crmProject?.closeMatches?.length} similar configurations</Badge>
                            )}
                            {crmProject.duplicates > 0 && (
                                <Badge className='mr-2 bg-red-500'>Had: {crmProject.duplicates} duplicates</Badge>
                            )}
                            {(() => {
                                if (crmProject.crmProjectExists) {
                                    return <Badge className='bg-green-500'>Project exists</Badge>
                                }
                                if (crmProject.closeMatches && crmProject.closeMatches.length > 0) {
                                    return <Badge className='bg-yellow-400'>Project has close matches ({crmProject.closeMatches.length})</Badge>
                                }
                                return <Badge className='bg-red-500'>New project</Badge>
                            })()}
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className='flex flex-col gap-2'>
                    <div className="flex items-center">
                        <strong className='text-xs'>CRM Project config details:</strong>
                        <Button size='sm' variant={'outline'} className='ml-auto' onClick={editHandler(String(crmProject.id))}>
                            Edit
                        </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {crmProject.selected_configuration?.parameter?.map((param) => {
                            const paramDefinition = crm?.configuration?.parameter?.project?.find(p => p.id === param.id);
                            return (
                                <BorderBox key={param.id} className='flex-col items-start bg-white' size='sm'>
                                    <p className='text-xs font-medium'>{paramDefinition?.name || param.id}:</p>
                                    <p className='text-xs'>{param.value}</p>
                                </BorderBox>
                            )
                        })}
                    </div>
                    <strong className='text-xs'>CRM Survey configurations:</strong>
                    <div className="flex flex-col gap-2">
                        {crmProject.crm_config.map((config) => (
                            <BorderBox key={config.id} className='' size='sm'>
                                <p>{config.name}</p>
                                <div className="flex items-center gap-2 ml-auto">
                                    {(config?.closeMatches?.length ?? 0) > 0 && (
                                        <Badge className='mr-2 bg-yellow-500'>{config?.closeMatches?.length} similar configurations</Badge>
                                    )}
                                    {config.duplicates > 0 && (
                                        <Badge className='mr-2 bg-red-500'>Had: {config.duplicates} duplicates</Badge>
                                    )}
                                    <Button size='sm' variant={'outline'} onClick={editHandler(String(config.id))}>
                                        Edit
                                    </Button>
                                </div>
                            </BorderBox>
                        ))}
                    </div>

                </AccordionContent>
            </AccordionItem>
        </BorderBox>
    )
}

function EditTab() {
    const {
        setCurrentTab,
        crm,
        fileObjectKeys,
        fileObjectExampleValues,
        isEditedConfigCrmProject,
        editedConfig
    } = useImportConfigsDialogContext();
    const form = useForm<EditConfigDialogTabSchemaType>({
        resolver: zodResolver(editConfigDialogTabSchema),
        mode: 'onChange',
        defaultValues: {
            name: editedConfig?.name || '',
            configParameters: editedConfig
                ? editedConfig.selected_configuration?.parameter?.reduce((acc, param) => {
                    acc[param.id ?? ''] = undefined;
                    return acc;
                }, {} as Record<string, string | undefined>)
                : {}
        }
    })
    const handleSubmit = (data: EditConfigDialogTabSchemaType) => {
        // const scrappedObject = {
        //     ...data,
        //     crmProjectFilePropertyToParameterMap: data.crmProjectFilePropertyToParameterMap ? scrapUndefinedValuesFromObject(data.crmProjectFilePropertyToParameterMap) : undefined,
        //     crmConfigFilePropertyToParameterMap: data.crmConfigFilePropertyToParameterMap ? scrapUndefinedValuesFromObject(data.crmConfigFilePropertyToParameterMap) : undefined,
        // } as EditConfigDialogTabSchemaType;
        // setFileObjectKeyToPropertyMap?.(scrappedObject);
        setCurrentTab('validate');
    }
    return (
        <TabsContent value="edit" className='space-y-2'>
            <DialogHeader>
                <DialogTitle className=''>
                    Edit configuration
                </DialogTitle>
            </DialogHeader>
            <Separator />
            <Form {...form}>
                <form>
                    <p className='font-medium'>CRM {isEditedConfigCrmProject ? "Project" : "Config"} Properties:</p>
                    <BorderBox className="grid items-start grid-cols-3 gap-2">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className='text-xs'>CRM {isEditedConfigCrmProject ? "Project" : "Config"} name *</FormLabel>
                                    <FormControl>
                                        {/* <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger>
                                            {field.value ?? "Select property from your file"}
                                        </SelectTrigger>
                                        <SelectContent>
                                            {fileObjectKeys.map((key, index) => (
                                                <SelectItem key={key} value={key}>
                                                    {key} <span className='text-xs italic text-muted-foreground'>(e.g. {String(fileObjectExampleValues[index])})</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select> */}
                                        <Combobox
                                            mode='single'
                                            items={fileObjectKeys.map(key => ({ label: key, value: key }))}
                                            selectedValue={field.value}
                                            setSelectedValue={(value: string | null) => {
                                                field.onChange(value)
                                            }}
                                            onCreate={(value) => {
                                                field.onChange(value)
                                            }}
                                            triggerLabel={field.value ?? "Select property from your file or define your own"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {(isEditedConfigCrmProject ? crm?.configuration?.parameter?.project : crm?.configuration?.parameter?.survey)?.map((param) => (
                            <FormField
                                key={param.id}
                                control={form.control}
                                name={`configParameters.${param.id}`}
                                render={({ field }) => (
                                    <FormItem className=''>
                                        <FormLabel className='text-xs'>{param.name}</FormLabel>
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger>
                                                    {field.value ?? "Select property from your file"}
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {fileObjectKeys.map((key, index) => (
                                                        <SelectItem
                                                            key={key}
                                                            value={key.trim()}
                                                        >
                                                            <span>{key} </span><span className='text-xs italic text-muted-foreground'>(e.g. {String(fileObjectExampleValues[index])})</span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />

                                    </FormItem>
                                )}
                            />
                        ))}
                    </BorderBox>
                </form>
            </Form>
            <DialogFooter className='flex justify-end gap-2 mt-2'>
                <Button
                    variant='secondary'
                    size='sm'
                    type='button'
                    onClick={() => { setCurrentTab('validate') }}
                >
                    Back
                </Button>
                <Button
                    variant='default'
                    size='sm'
                    type='button'
                >
                    Save
                </Button>
            </DialogFooter>
        </TabsContent>
    )
}

